import bind from 'bind-decorator';
import i18next from 'i18next';
import isEqual from 'lodash/isEqual';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { SmartArray } from '@babylonjs/core/Misc/smartArray';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { computed, reaction, IReactionDisposer, makeObservable, observable, action } from 'mobx';

import { isDeletableElement, isSelectableElement, elementCategories, addDimensionInfo, marginsToValidIntegers, selectableElementsCount, SnapshotOptions, SnapshotResult } from '../utils/SceneUtils';

import { AppStateManager, SelectionManager } from '@egr/xbox/base-app/managers/Manager';
import { AsyncFunction } from '@egr/xbox/utils/Types';
import { CropMargins } from '@egr/xbox/utils/Canvas';
import { ImageFormat } from '@egr/xbox/utils/FileFormats';
import { ImageSize } from '@egr/xbox/utils/Image';
import { TrackingEventFullScreen } from '@egr/xbox/telemetry/TrackingEvents';
import { addBreadcrumb, captureException } from '@egr/xbox/utils/Error';
import { assert, methodWithDebugTimer } from '@egr/xbox/utils/Debug';
import { breakpointObserver } from '@egr/xbox/egr-gui-elements/Helper/WindowSize';
import { default as BaseApp, OpenSessionEvent, CloseSessionEvent } from '@egr/xbox/base-app/App';
import { elementHasSize } from '@egr/xbox/egr-gui-utils/MiscHelper';
import { getEnvVar, getEnvBoolean, getEnvValue } from '@egr/xbox/utils/ReactScriptHelper';
import { inIframe, isMobileDevice, deviceHasMouse } from '@egr/xbox/utils/UserAgent';
import { isMainElement } from '@egr/xbox/base-app/Share';
import { layoutInformation } from '@egr/xbox/egr-gui-elements/Helper/Darkmode';
import { requireValidSession } from '@egr/xbox/base-app/decorators/RequireValidSession';
import { setFullscreenDimension } from '@egr/xbox/telemetry/TrackingUtils';
import { statusBarHeightRef } from '@egr/xbox/egr-gui-elements/Helper/StyleHelper';
import { trackCameraSwitch } from '@egr/xbox/telemetry/utils/TrackingCameraUtils';
import { trackEvent } from '@egr/xbox/telemetry/MatomoUtils';
import { withProgress } from '@egr/xbox/progress-manager/ProgressManager';

import { AppInitOptions } from '@egr/wcf/modules/core';
import { ArticleConfiguration } from '@egr/wcf/modules/bsk/cfg';
import { ArticleImageCameraSetup } from '@egr/wcf/modules/bsk';
import { CameraControl, CameraMode, ScreenshotOptions, View, Viewer } from '@egr/wcf/modules/core/view';
import { Default } from '@egr/wcf/modules/pl/tool/Default';
import { DeleteElements, InsertElements } from '@egr/wcf/modules/core/cmd';
import { ElementCategory, SceneElement } from '@egr/wcf/modules/core/mdl';
import { ListenableEvent } from '@egr/wcf/modules/utils/ListenableEvent';
import { OapErrorEventData, MainArticleElement, ArticleElement, BoundingBoxDimensions, ArticleManager, ArticleScreenshotOptions } from '@egr/wcf/modules/cf';
import { SelectionDefault } from '@egr/wcf/modules/core/tool/SelectionDefault';
import { ShadowMapping } from '@egr/wcf/modules/core/rendering/ShadowMapping';
import { ShadowPlane } from '@egr/wcf/modules/core/rendering/ShadowPlane';
import { isNotNullOrEmpty } from '@egr/wcf/modules/utils/string';
import { placeElementsInViewCenter } from '@egr/wcf/modules/pl/utils';

export const PRESERVE_DRAWING_BUFFER: boolean = getEnvBoolean('PRESERVE_DRAWING_BUFFER', true);
const SHOW_SCENE_DIMENSION: boolean = getEnvBoolean('SHOW_SCENE_DIMENSION', false);
export const SHOW_INTERACTORS_BY_DEFAULT: boolean = getEnvBoolean('SHOW_INTERACTORS_BY_DEFAULT', true);

const ENABLE_WEBGL2_SUPPORT: 'true' | 'false' | 'desktop' = getEnvValue('ENABLE_WEBGL2_SUPPORT', ['true', 'false', 'desktop'], 'true');

export interface SceneManagerState {
    articleCount: number;
    bottomOffset: number | undefined;
    cameraMode: CameraMode;
    canRedo: boolean;
    canUndo: boolean;
    /**
     * selectable MainArticleElements
     */
    elementCount: number;
    fullscreen: boolean;
    objectsOutOfBounds: boolean;
    rectSelect: boolean;
    selectedCategories: Array<ElementCategory>;
    showDimensions: boolean;
    centerCamera: boolean;

    showInteractors: boolean;
    disableInteractors: boolean;

    singleMainElementSelected: boolean;
    subArticleSelected: boolean;
    /**
     * selectable MainArticleElements + SubArticleElements
     */
    selectedableElementsCount: number;

    shadowMappingAdded: boolean;
}

export const RADIANT_BACKGROUND: string = getEnvVar('RADIANT_BACKGROUND', '');

type ArticleManagerCallback = {
    callback: () => void;
    isAsync: false;
} | {
    callback: () => Promise<void>;
    isAsync: true;
};

type ArticleFilter = (element: SceneElement) => element is MainArticleElement;
type SceneElementFilter = (element: SceneElement) => boolean;

export class SceneManager<App extends BaseApp = BaseApp> extends AppStateManager<SceneManagerState, App> {
    protected articleFilter: ArticleFilter = isMainElement;
    protected elementFilter: SceneElementFilter = isSelectableElement;

    @observable.ref
    protected elements: ReadonlyArray<SceneElement> = [];

    public readonly selectionManager: SelectionManager<SceneElement> = new SelectionManager();

    public readonly eventOapError: ListenableEvent< OapErrorEventData, AppStateManager<SceneManagerState>> =
        new ListenableEvent< OapErrorEventData, AppStateManager<SceneManagerState>>(this);

    public readonly viewerID: string = 'scene-dummy';

    public get appInitOptions(): Partial<AppInitOptions> {
        return {
            adaptToDeviceRatio: true,
            autoResizeViewer: false,
            hardwareAntialiasing: true,
            disableWebGL2Support: ENABLE_WEBGL2_SUPPORT === 'false' || (ENABLE_WEBGL2_SUPPORT === 'desktop' && isMobileDevice),
            preserveDrawingBuffer: PRESERVE_DRAWING_BUFFER
        };
    }

    public get selectableElements(): Array<ArticleElement> {
        const result: Array<ArticleElement> = [];
        const articles: Array<MainArticleElement> = this.app.coreApp.model.elements.filter(this.articleFilter);
        for (const article of articles) {
            result.push(article);
            result.push(...article.getSubArticles(false).filter((value) => value.canSelect()));
        }
        return result;
    }

    @computed
    public get mainElementSelection(): Array<MainArticleElement> {
        return this.selectionManager.selection.filter(this.articleFilter);
    }

    public get articleConfiguration(): ArticleConfiguration {
        if (this.internalArticleConfiguration == null) {
            this.internalArticleConfiguration = new ArticleConfiguration(this.app.eaiwsBasket);
        }
        return this.internalArticleConfiguration;
    }

    public get articleManagerCreated(): boolean {
        return this.articleManagerRef != null;
    }

    public get viewerCreated(): boolean {
        return this.viewerRef != null;
    }

    public get viewer(): Viewer {
        return this.viewerRef ?? this.getViewer();
    }

    public get activeViewSize(): ImageSize {
        return {
            width: this.viewer.view.renderWidth ?? window.innerWidth,
            height: this.viewer.view.renderHeight ?? window.innerHeight,
        };
    }

    protected articleManagerCallbacks: Array<ArticleManagerCallback> = [];
    protected containerRect: { width: number, height: number } | undefined;

    protected readonly defaultCamera: CameraMode = CameraMode.Perspective;
    protected readonly defaultCameraPosition: Vector3 = new Vector3(-1.5, 1.2, -3);

    protected lastNone2DCamera: CameraMode = CameraMode.Perspective;
    protected cameraWasSet: Array<CameraMode> = [];
    protected sceneBoundingBox: BoundingBoxDimensions | undefined;
    protected articleManagerRef: ArticleManager | undefined;

    private viewerRef: Viewer | undefined;
    private internalArticleConfiguration: ArticleConfiguration | null;
    private interactorsReaction: IReactionDisposer | undefined;

    /*
        TODO: Handling of orientation change
    */
    constructor(
        protected oapPlanningMode: boolean,
        public readonly multiSelectAutoStartEnabled: boolean = false
    ) {
        super();
        makeObservable(this);
        this.undo = this.undo.bind(this);
        this.redo = this.redo.bind(this);
        this.zoomToFit = this.zoomToFit.bind(this);
        this.removeSelection = this.removeSelection.bind(this);
        this.onCountChange = this.onCountChange.bind(this);
        this.onCloseSession = this.onCloseSession.bind(this);
        this.onOpenSession = this.onOpenSession.bind(this);
        this.updateSelection = this.updateSelection.bind(this);
        this.setCamera = this.setCamera.bind(this);
        this.selectParent = this.selectParent.bind(this);
        this.toggleDimensions = this.toggleDimensions.bind(this);
        this.showDimensions = this.showDimensions.bind(this);
        this.disableMultiSelect = this.disableMultiSelect.bind(this);
        this.enableMultiSelect = this.enableMultiSelect.bind(this);
        this.updateDimensionsVisiblity = this.updateDimensionsVisiblity.bind(this);

        this.resetCachedArticleInfos = this.resetCachedArticleInfos.bind(this);

        this.onRectSelectFinished = this.onRectSelectFinished.bind(this);
        this.startRectSelect = this.startRectSelect.bind(this);
        this.stopRectSelect = this.stopRectSelect.bind(this);
        this.toggleRectangleSelect = this.toggleRectangleSelect.bind(this);

        this.resizeViewer = this.resizeViewer.bind(this);

        this.leaveFullscreen = this.leaveFullscreen.bind(this);
        this.enterFullscreen = this.enterFullscreen.bind(this);
        this.toggleFullscreen = this.toggleFullscreen.bind(this);

        this.getSnapshot = this.getSnapshot.bind(this);

        this.getArticleManager = this.getArticleManager.bind(this);
        this.createArticleManager = this.createArticleManager.bind(this);
        this.onArticleManagerCreation = this.onArticleManagerCreation.bind(this);
        this.initializeArticleManagerSession = this.initializeArticleManagerSession.bind(this);
    }

    public override initialize(app: App): void {
        super.initialize(app);
        app.coreApp.commands.eventHistoryChanged.addListener(this.updateHistory);
        app.events.session.close.addListener(this.onCloseSession);
        app.events.session.open.addListener(this.onOpenSession);
        setFullscreenDimension(false);
    }

    @bind
    public async sceneContainerHasValidSize(): Promise<boolean> {
        const sceneContainer: HTMLElement | null = document.getElementById(this.viewerID);

        if (sceneContainer == null) {
            return false;
        }

        if (await elementHasSize(sceneContainer, 50, 20000)) {
            this.handleResize();
            return true;
        }

        return false;
    }

    public getViewer(): Viewer {
        if (this.viewerRef == null) {
            const viewContainer: HTMLElement | null = document.getElementById(this.viewerID);

            assert(viewContainer != null && viewContainer instanceof HTMLDivElement, `Element with ID: ${this.viewerID} should exist in DOM`);

            this.app.coreApp.initialize.bind(this.app.coreApp)(viewContainer as HTMLDivElement, this.appInitOptions);

            if (this.app.coreApp.viewer.renderingEnabled) {
                this.app.coreApp.viewer.beginDisableRendering();
            }

            this.viewerRef = this.app.coreApp.viewer;

            this.viewerRef.eventAfterRender.addListener(this.updateOutOfBounds);
            this.viewerRef.eventResize.addListener(this.zoomToFit);
            CameraControl.DEFAULT_CAMERA_POSITION = this.defaultCameraPosition;
            const view: View = this.viewer.view;
            view.cameraControl.orbitInertia = 0.95;
            view.cameraControl.orthoOrbitSnappingEnabled = true;

            this.app.coreApp.rendering.addShadowGenerator(new ShadowPlane(this.app.coreApp));

            // disabled because of low performance on mobile devices
            if (getEnvBoolean('SHADOW_MAPPING', true) && !isMobileDevice) {
                this.app.coreApp.rendering.addShadowGenerator(new ShadowMapping(this.app.coreApp));
                this.updateState({ shadowMappingAdded: true });
            }

            this.app.coreApp.model.eventSelectionChanged.addListener(this.updateSelection);

            view.cameraControl.dblClickZoomToFitOptions.margin = .3;
            view.cameraControl.dblClickZoomToFitOptions.minMarginPxH = 16;
            view.cameraControl.dblClickZoomToFitOptions.minMarginPx =
                this.state.bottomOffset == null ? undefined : this.state.bottomOffset * 2;

            view.cameraControl.eventCameraModeChanged.addListener(this.updateCameraState);

            this.updateCameraState();

            this.app.coreApp.model.scene.clearColor = new Color4(0, 0, 0, 0);

            if (inIframe()) {
                this.app.coreApp.scene.preventDefaultOnPointerDown = false;
                this.app.coreApp.scene.preventDefaultOnPointerUp = false;
            }

            reaction(
                () => this.state.bottomOffset,
                () => {
                    view.cameraControl.dblClickZoomToFitOptions.minMarginPx =
                        this.state.bottomOffset == null ? undefined : this.state.bottomOffset * 2;
                }
            );

            this.app.coreApp.tools.eventToolStarted.addListener(this.updateTool);

            this.app.events.viewCreated.trigger();
        }

        return this.viewerRef;
    }

    public async getArticleManager(): Promise<ArticleManager> {
        return this.articleManagerRef ?? this.createArticleManager();
    }

    public getArticleManagerSync(): ArticleManager | null {
        return this.articleManagerRef ?? null;
    }

    /**
     * If articleManager is already created callback will be called instantly, else it will be called after creation
     * @param callback
     */
    public addArticleManagerCallback(callback: AsyncFunction, isAsync: true): Promise<void>;
    public addArticleManagerCallback(callback: VoidFunction, isAsync: false): void;
    public addArticleManagerCallback(callback: AsyncFunction | VoidFunction, isAsync: boolean = false): void | Promise<void> {
        if (this.articleManagerCreated) {
            return callback();
        }

        if (isAsync) {
            this.articleManagerCallbacks.push({callback: callback as AsyncFunction, isAsync: true});
        } else {
            this.articleManagerCallbacks.push({callback, isAsync});
        }

    }

    @bind
    public handleResize(): void {
        if (this.viewerCreated) {
            this.app.coreApp.viewer.resize();
            const containerRect: DOMRect = this.app.coreApp.viewer.container.getBoundingClientRect();
            this.containerRect = { width: containerRect.width, height: containerRect.height};
        }
    }

    @computed
    public get interiorSelected(): boolean {
        return this.state.selectedCategories.indexOf(ElementCategory.Interior) !== -1;
    }

    @computed
    public get selectionName(): string {
        if (this.selectionManager.selection.length > 1) {
            return i18next.t('Multiple elements');
        }

        return i18next.t('3D Model');
    }

    public startRectSelect(): void {
        if (!deviceHasMouse()) {
            this.app.coreApp.tools.startDefaultTool();
            (this.app.coreApp.tools.defaultTool as Default).startRectangleSelect();
            return this.updateState({rectSelect: true});
        }

        if (this.app.coreApp.tools.defaultTool instanceof Default) {
            this.app.coreApp.tools.defaultTool.rectangleSelectMode = 'Both';
            this.app.coreApp.tools.defaultTool.leftMouseButtonRectangleSelectEnabled = true;
        }

        this.app.coreApp.viewer.view.cameraControl.leftMouseButtonEnabled = false;

        this.updateState({ rectSelect: true });
    }

    public stopRectSelect(): void {
        this.updateState({ rectSelect: false });

        this.app.coreApp.tools.stopActiveTool();

        if (this.app.coreApp.tools.defaultTool instanceof Default) {
            this.app.coreApp.tools.defaultTool.rectangleSelectMode = 'Complete';
            this.app.coreApp.tools.defaultTool.leftMouseButtonRectangleSelectEnabled = false;
        }
        this.app.coreApp.viewer.view.cameraControl.leftMouseButtonEnabled = true;

    }

    public toggleRectangleSelect(): void {
        if (this.state.rectSelect) {
            this.stopRectSelect();
        } else {
            this.startRectSelect();
        }
    }

    public async undo(): Promise<void> {
        if (this.app.coreApp.commands.canUndo) {
            this.stopActiveTool();
            await this.app.coreApp.commands.undo();
        }
        trackEvent({
            category: 'Project',
            action: 'undo'
        });
    }

    public async redo(): Promise<void> {
        if (this.app.coreApp.commands.canRedo) {
            this.stopActiveTool();
            await this.app.coreApp.commands.redo();
        }
        trackEvent({
            category: 'Project',
            action: 'redo'
        });
    }

    @action
    public onCountChange(): void {
        if (this.app.coreApp.model == null) {
            return;
        }

        this.elements = this.app.coreApp.model.elements;

        const articles: Array<MainArticleElement> = this.app.coreApp.model.elements.filter(this.articleFilter);

        (this.app.coreApp.tools.defaultTool as Default).multiSelectAutoStartEnabled = this.multiSelectAutoStartEnabled && articles.length > 1;

        this.updateState({
            articleCount: articles.length,
            elementCount: this.app.coreApp.model.elements.filter(this.elementFilter).length,
            selectedableElementsCount: articles.reduce(selectableElementsCount, 0),
        });
    }

    public set2D(enable: boolean): void {
        if (enable) {
            if (this.state.cameraMode !== CameraMode.Top) {
                this.lastNone2DCamera = this.state.cameraMode;
            }
            this.setCamera(CameraMode.Top);
        } else {
            this.setCamera(this.lastNone2DCamera);
        }
    }

    public setCamera(camera: CameraMode | undefined): void {
        if (camera == null) {
            camera = this.defaultCamera;
        }

        if (camera != null) {
            if (this.viewer.view.cameraControl.cameraMode === camera) {
                return;
            }
            this.viewer.view.cameraControl.cameraMode = camera;
            trackCameraSwitch(camera);
        }

        if (camera != null && !this.cameraWasSet.includes(camera)) {
            this.zoomToFit();
            this.cameraWasSet.push(camera);
        }
    }

    @computed
    public get is2D(): boolean {
        return !(
            this.state.cameraMode === CameraMode.Orthographic ||
            this.state.cameraMode === CameraMode.Perspective
        );
    }

    @computed
    public get rotatable(): boolean {
        return !(
            this.state.cameraMode === CameraMode.Top ||
            this.state.cameraMode === CameraMode.Bottom ||
            this.state.cameraMode === CameraMode.Right ||
            this.state.cameraMode === CameraMode.Left ||
            this.state.cameraMode === CameraMode.Front ||
            this.state.cameraMode === CameraMode.Back
        );
    }

    public zoomToFit(): void {
        if (this.viewerCreated) {
            this.viewer.view.cameraControl.zoomToFitElements(
                null,
                this.viewer.view.cameraControl.dblClickZoomToFitOptions
            );
        }
    }

    @withProgress('remove selected elements')
    public async removeSelection(): Promise<void> {
        const deletableElements: Array<SceneElement> = this.app.coreApp.model.selection.filter(isDeletableElement);
        if (deletableElements.length !== 0) {
            this.app.coreApp.tools.stopActiveTool();
            const command: DeleteElements = new DeleteElements(
                this.app.coreApp,
                deletableElements
            );
            if (command.isAvailable()) {
                try {
                    await this.app.coreApp.commands.executeCommand(command);
                } catch (error) {
                    captureException(error, 'error');
                }
            }
        }
    }

    @bind
    @withProgress('remove all elements')
    public async clearScene(synchronizeSession: boolean = false): Promise<void> {
        if (this.viewerCreated) {
            this.app.coreApp.tools.stopActiveTool();

            const elements: Array<SceneElement> = this.app.coreApp.model.elements.filter(isDeletableElement);

            if (elements.length === 0) {
                return;
            }

            const command: DeleteElements = new DeleteElements(
                this.app.coreApp,
                elements
            );

            if (command.isAvailable()) {
                try {
                    await this.app.coreApp.commands.executeCommand(command);
                } catch (error) {
                    captureException(error, 'error');
                }
            }

            if (synchronizeSession && this.articleManagerRef != null) {
                await this.articleManagerRef.synchronizeSession(true);
            }
        }
    }

    public selectParent(): void {
        if (this.app.coreApp.model.selection.length !== 0) {
            this.app.coreApp.model.selectParentSubElement(this.app.coreApp.model.selection[0]);
        }
    }

    public toggleDimensions(): void {
        this.showDimensions(this.state.showDimensions === false);
    }

    public showDimensions(showDimensions: boolean): void {
        this.updateState({showDimensions});
        this.updateDimensionsVisiblity();
    }

    @bind
    public showInteractors(showInteractors: boolean = true): void {
        this.updateState({showInteractors});
    }

    @bind
    public disableInteractors(disableInteractors: boolean = true): void {
        this.updateState({disableInteractors});
    }

    public onRectSelectFinished(): void {
        if (
            !deviceHasMouse() &&
            this.state.rectSelect
        ) {
            if (this.app.coreApp.model.selection.length > 1 && !this.selectionManager.state.enabled) {
                if (this.app.coreApp.tools.defaultTool instanceof Default) {
                    this.app.coreApp.tools.defaultTool.multiSelectMode = true;
                }
                this.updateState({rectSelect: false});
            } else {
                return this.startRectSelect();
            }
        }
    }

    public resizeViewer(): void {
        if (this.viewerCreated) {
            requestAnimationFrame(
                () => {
                    const containerRect: DOMRect = this.app.coreApp.viewer.container.getBoundingClientRect();
                    if (!isEqual(this.containerRect, { width: containerRect.width, height: containerRect.height})) {
                        this.handleResize();
                    }
                }
            );
        }
    }

    public disableMultiSelect(): void {
        if (this.app.coreApp.tools.activeTool instanceof SelectionDefault) {
            this.app.coreApp.tools.activeTool.multiSelectMode = false;
        }
    }

    public enableMultiSelect(): void {
        if (this.app.coreApp.tools.activeTool instanceof SelectionDefault) {
            this.app.coreApp.tools.activeTool.multiSelectMode = true;
        }
    }

    public setSelection(selection: ReadonlyArray<SceneElement> | null): boolean {
        if (selection != null && selection[0] instanceof MainArticleElement) {
            addBreadcrumb({
                category: 'SelectionID',
                message: selection[0].basketId!
            });
        }
        return this.app.coreApp.model.setSelection(selection);
    }
    public leaveFullscreen(name: TrackingEventFullScreen['name']): void {
        if (!this.state.fullscreen && !this.viewerCreated) {
            return;
        }

        this.updateState({fullscreen: false});

        trackEvent({
            category: 'Full Screen',
            action: 'leave',
            name
        });

        this.resizeViewer();
    }

    public enterFullscreen(name: TrackingEventFullScreen['name']): void {
        if (this.state.fullscreen && !this.viewerCreated) {
            return;
        }

        this.updateState({fullscreen: true});

        trackEvent({
            category: 'Full Screen',
            action: 'enter',
            name
        });

        this.resizeViewer();
    }

    public toggleFullscreen(name: TrackingEventFullScreen['name']): void {
        if (this.state.fullscreen) {
            this.leaveFullscreen(name);
        } else {
            this.enterFullscreen(name);
        }
    }

    public getSnapshotMargins(minMargins?: CropMargins): CropMargins | undefined {
        return marginsToValidIntegers(minMargins);
    }

    @withProgress('create snapshot')
    public async getSnapshot(options: SnapshotOptions): Promise<SnapshotResult> {
        const size: ImageSize = this.app.imageScaleManager.getImageDimension(this.activeViewSize);

        const pixelScaleFactor: number = this.get3dCanvasPixelRatio();

        let imageFormat: ImageFormat = ImageFormat.JPG;

        const screenshotOptions: ScreenshotOptions = {
            ...size,
            pixelScaleFactor,
            mimeType: ImageFormat.JPG.mimetype,
            backgroundColor: layoutInformation.isDarkmodeActive ?
                Color4.FromHexString(options.darkBackgroundColor ? options.darkBackgroundColor : '#3A3A3A') :
                Color4.FromHexString(options.lightBackgroundColor ? options.lightBackgroundColor : '#FFFFFF')
        };

        if (options.transparent) {
            screenshotOptions.mimeType = ImageFormat.PNG.mimetype;
            screenshotOptions.backgroundColor = new Color4(0, 0, 0, 0);
            imageFormat = ImageFormat.PNG;
        }

        if (options.withDimensions) {
            screenshotOptions.onDrawOverlay = addDimensionInfo(
                pixelScaleFactor,
                breakpointObserver.smallLayout ? 10 : 12,
                this.getSnapshotMargins(options.margins),
            );
        }

        try {
            return {
                result: await this.app.coreApp.viewer.createScreenshot(
                    screenshotOptions,
                    true
                ),
                size,
                imageFormat
            };
        } catch (error) {
            captureException(error, 'error');
            this.app.messageManager.showMessage(i18next.t('Action failed'));
            return { result: null, size, imageFormat };
        }
    }

    public async getArticleSnapshot(
        article: ArticleElement | null,
        mimeType: 'image/png' | 'image/jpeg',
        cameraSetup: ArticleImageCameraSetup | null,
        asBlob: true
    ): Promise<Blob | null>;
    public async getArticleSnapshot(
        article: ArticleElement | null,
        mimeType: 'image/png' | 'image/jpeg',
        cameraSetup: ArticleImageCameraSetup | null,
        asBlob: false
    ): Promise<string | null>;
    @bind
    public async getArticleSnapshot(
        article: ArticleElement | null,
        mimeType: 'image/png' | 'image/jpeg' = 'image/png',
        cameraSetup: ArticleImageCameraSetup | null = null,
        asBlob: boolean = true,
    ): Promise<Blob | string | null> {
        if (article == null || article.node == null) {
            return null;
        }

        const articleScreenshotOptions: ArticleScreenshotOptions = {
            width: 1000,
            height: 1000,
            pixelScaleFactor: this.get3dCanvasPixelRatio(),
            backgroundColor: mimeType === 'image/png' ? new Color4(0, 0, 0, 0) : new Color4(1, 1, 1, 1),
            mimeType
        };

        const articleManager: ArticleManager = await this.getArticleManager();

        if (cameraSetup == null) {
            cameraSetup = this.articleConfiguration.getArticleImageCamera(article);
        }
        if (cameraSetup != null) {
            articleScreenshotOptions.orthographic = cameraSetup.mode === 'Orthographic';
            articleScreenshotOptions.cameraAlpha = cameraSetup.alpha;
            articleScreenshotOptions.cameraBeta = cameraSetup.beta;
            articleScreenshotOptions.cameraFov = cameraSetup.fov;
        }

        await this.app.coreApp.model.materials.waitUntilTexturesLoaded();

        // if the user cancels configuration while textures are loading,
        // the article is undefined now and createArticleScreenshot would throw exception
        if (article == null || article.node == null) {
            return null;
        }

        return articleManager.createArticleScreenshot(article, articleScreenshotOptions, asBlob);
    }

    @bind
    public get3dCanvasPixelRatio(): number {
        if (this.app.coreApp.initOptions.adaptToDeviceRatio) {
            return Math.min(window.devicePixelRatio, this.app.coreApp.initOptions.maximumDeviceRatio);
        }

        return 1.0;
    }

    public disableRendering(): void {
        if (this.app.coreApp.viewer?.renderingEnabled) {
            this.app.coreApp.viewer.beginDisableRendering();
        }
    }

    /**
     * Needed because there may be multiple beginDisableRendering calls before, and endDisableRendering needs to be called as offen
     */
    @bind
    public enableRendering(): void {
        this.viewer.endDisableRendering();

        for (let i: number = 0; i < 10; i++) {
            if (this.viewer.renderingEnabled) {
                return;
            }
            this.viewer.endDisableRendering();
        }
    }

    public async resetCachedArticleInfos(): Promise<void> {
        const articleManager: ArticleManager = await this.getArticleManager();
        return articleManager.resetCachedArticleInfos(true);
    }

    /**
     * do not override without check if already called before
     */
    @methodWithDebugTimer('create article manager')
    public async createArticleManager(): Promise<ArticleManager> {
        if (this.articleManagerRef == null) {
            this.getViewer();

            this.articleManagerRef = new ArticleManager(this.app.coreApp, this.app.eaiwsSession);

            await this.onArticleManagerCreation(this.articleManagerRef);
        }

        return this.articleManagerRef;
    }

    public updateDimensionsVisiblity(): void {
        const articles: Array<MainArticleElement> = this.app.coreApp.model.elements.filter(this.articleFilter);
        const articleManager: ArticleManager | null = this.getArticleManagerSync();

        this.sceneBoundingBox?.dispose();
        delete this.sceneBoundingBox;

        if (SHOW_SCENE_DIMENSION && articles.length > 1) {
            if (this.state.showDimensions && articleManager != null) {
                this.createSceneBoundingBox(articleManager);
            }
        } else if (articles.length === 1) {
            const element: MainArticleElement = articles[0];
            element.showDimensions = this.state.showDimensions && articles.length === 1;
        }
    }

    protected updateSelection(): void {
        this.selectionManager.setSelection(this.app.coreApp.model.selection);
        this.updateState({
            singleMainElementSelected: (
                this.app.coreApp.model.selection.length === 1 &&
                this.app.coreApp.model.selection[0].category === ElementCategory.Interior
            ),
            subArticleSelected: (
                this.app.coreApp.model.hasSubElementSelection()
            ),
            selectedCategories: this.app.coreApp.model.selection.reduce(elementCategories, [])
        });
    }

    protected stopActiveTool(): void {
        if (
            this.app.coreApp.tools &&
            this.app.coreApp.tools.activeTool &&
            this.app.coreApp.tools.activeTool.isRunning()
        ) {
            this.app.coreApp.tools.activeTool.stop();
        }
    }

    protected getInitialState(): SceneManagerState {
        return {
            articleCount: 0,
            elementCount: 0,
            selectedableElementsCount: 0,
            cameraMode: CameraMode.Perspective,
            canRedo: false,
            canUndo: false,
            fullscreen: false,
            bottomOffset: isNotNullOrEmpty(RADIANT_BACKGROUND) ? (56 + statusBarHeightRef.get()) : undefined,
            objectsOutOfBounds: false,
            rectSelect: false,
            selectedCategories: [],
            showDimensions: false,
            singleMainElementSelected: false,
            subArticleSelected: false,
            centerCamera: false,

            showInteractors: SHOW_INTERACTORS_BY_DEFAULT,
            disableInteractors: false,

            shadowMappingAdded: false,
        };
    }

    protected onOpenSession(event: OpenSessionEvent): void {
        event.tasks.preProcessing.addTask('update history', async (): Promise<void> => {
            this.updateHistory();
        });
        event.tasks.postProcessing.addTask('reset camera history', async () => this.resetCameraHistory());
    }

    protected onCloseSession(event: CloseSessionEvent): void {
        this.articleManagerCallbacks.length = 0;
        this.updateState({
            ...this.getInitialState(),
            cameraMode: this.state.cameraMode,
            showInteractors: this.state.showInteractors,
            shadowMappingAdded: this.state.shadowMappingAdded,
            rectSelect: this.state.rectSelect
        });
    }

    @bind
    protected updateHistory(): void {
        this.updateState({
            canRedo: this.app.coreApp.commands.canRedo,
            canUndo: this.app.coreApp.commands.canUndo,
        });
    }

    @bind
    protected updateOutOfBounds(): void {
        const activeMeshes: SmartArray<AbstractMesh> = this.app.coreApp.model.scene.getActiveMeshes();
        for (let i: number = 0; i < activeMeshes.length; ++i) {
            if (activeMeshes.data[i].name !== 'shadowPlane' && !activeMeshes.data[i].alwaysSelectAsActiveMesh) {
                return this.updateState({objectsOutOfBounds: false});
            }
        }
        this.updateState({objectsOutOfBounds: true});
    }

    @bind
    protected updateCameraState(): void {
        this.updateState({cameraMode: this.viewer.view.cameraControl.cameraMode});
    }

    @bind
    protected resetCameraHistory(): void {
        this.cameraWasSet = [];
    }

    @withProgress('articleManager initializeSession')
    protected async initializeArticleManagerSession(): Promise<void> {
        if (this.articleManagerRef != null && this.app.eaiwsSession.isValid) {
            await this.articleManagerRef.initializeSession({oapPlanningMode: this.oapPlanningMode});
        }
    }

    @bind
    @withProgress('clone elements')
    @requireValidSession
    protected async cloneElements(elements: ReadonlyArray<SceneElement>): Promise<void> {
        if (elements.length !== 0) {
            this.app.coreApp.tools.stopActiveTool();
            const clones: Array<SceneElement> = [];
            for (const element of elements) {
                const newElement: SceneElement | null = element.clone();
                if (newElement != null) {
                    clones.push(newElement);
                }
            }

            placeElementsInViewCenter(
                clones, this.viewer.view, this.app.coreApp.model
            );

            const command: InsertElements = new InsertElements(this.app.coreApp, clones);
            await this.app.coreApp.commands.executeCommand(command);
            this.app.coreApp.model.setSelection(clones);
        }
    }

    @bind
    protected createSceneBoundingBox(articleManager: ArticleManager): void {
        this.sceneBoundingBox = new BoundingBoxDimensions(
            this.app.coreApp.model.elements,
            articleManager,
            {
                offsetFactor: 3
            }
        );
    }

    protected async onArticleManagerCreation(articleManager: ArticleManager): Promise<void> {
        for (const callback of this.articleManagerCallbacks) {
            if (callback.isAsync) {
                await callback.callback();
            } else {
                callback.callback();
            }
        }
        this.articleManagerCallbacks.length = 0;

        this.app.sessionManager.onChange.addListener(() => {
            articleManager?.resetCachedArticleInfos(true);
        });

        articleManager.eventArticleAdded.addListener(this.onCountChange);
        articleManager.eventArticleRemoved.addListener(this.onCountChange);
        articleManager.eventArticleAdded.addListener(this.resetCameraHistory);

        articleManager.eventArticleAdded.addListener(this.updateDimensionsVisiblity);
        articleManager.eventArticleRemoved.addListener(this.updateDimensionsVisiblity);

        this.app.events.session.open.addListener((event: OpenSessionEvent) => {
            event.tasks.postProcessing.addTask('SceneManager initializeArticleManagerSession', this.initializeArticleManagerSession);
            event.tasks.postProcessing.addTask('update count', async () => this.onCountChange());
        });

        articleManager.eventOapError.addListener((pParameter) => { this.eventOapError.trigger(pParameter); } );

        await this.initializeArticleManagerSession();

    }
    @bind
    private updateTool(): void {
        if (this.app.coreApp.tools.activeTool instanceof SelectionDefault) {
            this.app.coreApp.tools.activeTool.showElementInteractors = this.state.showInteractors && !this.state.disableInteractors;
        }

        this.interactorsReaction?.();

        this.interactorsReaction = reaction(
            () => {
                return this.state.showInteractors && !this.state.disableInteractors;
            },
            this.updateTool
        );
    }
}